import { all, call, fork, put, select, takeLatest } from 'redux-saga/effects';
import ApiConstants from 'src/common/helpers/ApiConstants';
import * as requests from 'src/common/helpers/HTTPRequest';
import { toastError, toastSuccess } from 'src/common/helpers/Notifications';
import TextConstants from 'src/common/helpers/TextConstants';
import {
  GROUP_DEVICES_ACTIONS,
  addGroupDevicesSuccess,
  getGroupDevicesError,
  getGroupDevicesSuccess,
  getGroupLoggersError,
  getGroupLoggersSuccess,
  unlinkGroupDeviceSuccess,
} from './actions';

function* getGroupDevicesSaga({ payload: groupId }) {
  try {
    const url = `${ApiConstants.BASE_URL}${ApiConstants.GROUPS_MANAGEMENT}/${groupId}/${ApiConstants.UNITS}`;
    const devices = yield call(requests.getRequest, url, {});

    yield put(getGroupDevicesSuccess({ groupId, devices }));
  } catch (e) {
    console.log(e);
    toastError(e.message);
    yield put(getGroupDevicesError(e));
  }
}

function* getGroupLoggersSaga({ payload: groupId }) {
  try {
    const url = `${ApiConstants.BASE_URL}${ApiConstants.GROUPS}/${groupId}/${ApiConstants.LOGGERS_MANAGEMENT}`;
    const response = yield call(requests.getRequest, url, {});

    yield put(getGroupLoggersSuccess(response));
  } catch (e) {
    console.log(e);
    toastError(e.message);
    yield put(getGroupLoggersError(e));
  }
}

function* addGroupDevicesSaga({ payload: { groupId, data, callback } }) {
  try {
    const url = `${ApiConstants.BASE_URL}${ApiConstants.GROUPS_MANAGEMENT}/${groupId}/${ApiConstants.UNITS}`;
    const response = yield call(requests.postRequest, url, data);

    // update device list on UnitsChannels reducer
    const { units } = yield select((state) => state.UnitsChannels);
    const updatedUnits = units.map((x) =>
      response.deviceIds?.includes(x.deviceId)
        ? { ...x, groupId: response.groupId }
        : x
    );

    // update loggers list on LoggersManagement reducer
    const { records: loggers } = yield select(
      (state) => state.LoggersManagement
    );
    const updatedLoggers = loggers.map((x) =>
      response.deviceIds?.includes(x.deviceId)
        ? {
            ...x,
            groupId: response.groupId,
            groupIds: [...(x.groupIds || []), response.groupId].filter(
              (id, index, self) => self.indexOf(id) === index
            ),
          }
        : x
    );

    const groupDevices = (
      response.deviceIds?.map((x) =>
        (updatedLoggers || []).find((logger) => logger.deviceId === x)
      ) || []
    ).filter((x) => !!x);

    yield put(
      addGroupDevicesSuccess({
        units: updatedUnits,
        devices: groupDevices,
        loggers: updatedLoggers,
      })
    );

    toastSuccess(
      TextConstants.GroupsManagement.GroupDevices.AddGroupLoggerSuccessMessage
    );

    callback && callback();
  } catch (e) {
    console.log(e);
    toastError(e.message);
    yield put({
      type: GROUP_DEVICES_ACTIONS.ADD_GROUP_DEVICES_ERROR,
    });
  }
}

function* unlinkGroupDeviceSaga({ payload: { groupId, deviceId, callback } }) {
  try {
    const url = `${ApiConstants.BASE_URL}${ApiConstants.GROUPS_MANAGEMENT}/${groupId}/${ApiConstants.UNITS}/${deviceId}`;
    yield call(requests.deleteRequest, url);

    // update device list on UnitsChannels reducer
    const { units } = yield select((state) => state.UnitsChannels);
    const updatedUnits = units.map((x) =>
      x.deviceId === deviceId ? { ...x, groupId: null } : x
    );

    // update logger list on LoggersManagement reducer
    const { records: loggers } = yield select(
      (state) => state.LoggersManagement
    );
    const updatedLoggers = loggers.map((x) =>
      x.deviceId === deviceId
        ? {
            ...x,
            groupId: null,
            groupIds: (x.groupIds || []).filter((id) => id !== groupId),
          }
        : x
    );

    yield put(
      unlinkGroupDeviceSuccess({
        deviceId,
        units: updatedUnits,
        loggers: updatedLoggers,
      })
    );

    toastSuccess(
      TextConstants.GroupsManagement.GroupDevices
        .UnlinkGroupLoggerSuccessMessage
    );

    callback && callback();
  } catch (e) {
    console.log(e);
    toastError(e.message);
    yield put({
      type: GROUP_DEVICES_ACTIONS.ADD_GROUP_DEVICES_ERROR,
    });
  }
}

export function* GroupDevicesManagementSaga() {
  yield takeLatest(
    GROUP_DEVICES_ACTIONS.GET_GROUP_DEVICES,
    getGroupDevicesSaga
  );
  yield takeLatest(
    GROUP_DEVICES_ACTIONS.GET_GROUP_LOGGERS,
    getGroupLoggersSaga
  );
  yield takeLatest(
    GROUP_DEVICES_ACTIONS.ADD_GROUP_DEVICES,
    addGroupDevicesSaga
  );
  yield takeLatest(
    GROUP_DEVICES_ACTIONS.UNLINK_GROUP_DEVICE,
    unlinkGroupDeviceSaga
  );
}

export default function* rootSaga() {
  yield all([fork(GroupDevicesManagementSaga)]);
}
